import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { configureMoney, formatR } from "@/lib/format";
import { translate } from "@/lib/i18n";

const LANG_KEY = "sofit_lang";
const CUR_KEY = "sofit_currency";
const Ctx = createContext(null);

export function PrefsProvider({ children }) {
  const { user, setUser } = useAuth();
  const [language, setLanguage] = useState(localStorage.getItem(LANG_KEY) || "en");
  const [currency, setCurrency] = useState(localStorage.getItem(CUR_KEY) || "ZAR");

  const rates = useQuery({ queryKey: ["rates"], queryFn: () => api("/rates", { token: null }), staleTime: 60 * 60 * 1000 });

  useEffect(() => {
    if (user?.language) setLanguage(user.language);
    if (user?.currency) setCurrency(user.currency);
  }, [user?.id, user?.language, user?.currency]);

  useEffect(() => {
    configureMoney(currency, rates.data?.rates ?? { ZAR: 1 });
  }, [currency, rates.data]);

  const setPrefs = useCallback(async (p, persistToServer = true) => {
    if (p.language) { setLanguage(p.language); localStorage.setItem(LANG_KEY, p.language); }
    if (p.currency) { setCurrency(p.currency); localStorage.setItem(CUR_KEY, p.currency); }
    if (user && persistToServer) {
      const u = await api("/me/preferences", { method: "PUT", body: p });
      setUser(u);
    }
  }, [user, setUser]);

  const value = useMemo(() => ({
    language, currency,
    rates: rates.data?.rates ?? { ZAR: 1 },
    ratesUpdatedAt: rates.data?.updated_at ?? null,
    setPrefs,
  }), [language, currency, rates.data, setPrefs]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePrefs() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePrefs outside PrefsProvider");
  return ctx;
}

export function useT() {
  const { language } = usePrefs();
  return useCallback((key) => translate(language, key), [language]);
}

export function useMoney() {
  const { currency, rates } = usePrefs();
  return { currency, rates, formatR };
}
